import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, CardHeader, Chip, Button, Switch } from "@heroui/react";
import { useLab } from "../lib/state.tsx";
import { fmtBytes, fmtTime, type LabEvent } from "../lib/api.ts";
import { Json } from "./Json.tsx";

const PROTOCOL_COLOUR: Record<string, "primary" | "secondary" | "default"> = {
  "2026-07-28": "primary",
  "2025-06-18": "secondary",
};

function FrameRow({ ev }: { ev: Extract<LabEvent, { kind: "frame" }> }) {
  const [open, setOpen] = useState(false);
  const inbound = ev.direction === "in";

  return (
    <div className="border-b border-default-100/40 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flash-in flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-default-100/30"
      >
        <span className={`font-mono text-xs ${inbound ? "text-primary" : "text-warning"}`}>
          {inbound ? "→" : "←"}
        </span>
        <Chip
          size="sm"
          variant="flat"
          color={PROTOCOL_COLOUR[ev.protocol] ?? "default"}
          className="h-5 shrink-0 font-mono text-[10px]"
        >
          {ev.protocol}
        </Chip>
        <span className="truncate font-mono text-xs text-default-200">{ev.method ?? "—"}</span>
        {ev.transport === "sse" && (
          <Chip size="sm" variant="bordered" className="h-5 font-mono text-[10px]">
            sse
          </Chip>
        )}
        <span className="ml-auto shrink-0 font-mono text-[10px] text-default-500">
          {fmtBytes(ev.bytes)}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-default-600">{fmtTime(ev.ts)}</span>
      </button>

      {open && (
        <div className="bg-default-50/40 px-3 pb-3">
          {ev.headers && Object.keys(ev.headers).length > 0 && (
            <div className="mb-2 border-l-2 border-warning/50 pl-2">
              {Object.entries(ev.headers).map(([k, v]) => (
                <div key={k} className="font-mono text-[10px] text-default-400">
                  <span className="text-warning">{k}</span>: {v}
                </div>
              ))}
            </div>
          )}
          <Json value={ev.payload} />
        </div>
      )}
    </div>
  );
}

function GateRow({ ev }: { ev: Extract<LabEvent, { kind: "gate" }> }) {
  const colour =
    ev.decision === "denied" ? "danger" : ev.decision === "allowed" ? "success" : "warning";
  return (
    <div className="flash-in flex items-center gap-2 border-b border-default-100/40 px-3 py-1.5 last:border-0">
      <Chip size="sm" color={colour} variant="flat" className="h-5 shrink-0 font-mono text-[10px]">
        {ev.gate}
      </Chip>
      <span className="truncate text-xs text-default-300">{ev.detail}</span>
      <span className="ml-auto shrink-0 font-mono text-[10px] text-default-600">
        {fmtTime(ev.ts)}
      </span>
    </div>
  );
}

function LogRow({ ev }: { ev: Extract<LabEvent, { kind: "log" }> }) {
  const colour =
    ev.level === "error" ? "text-danger" : ev.level === "warning" ? "text-warning" : "text-default-400";
  return (
    <div className="flash-in flex items-center gap-2 border-b border-default-100/40 px-3 py-1.5 last:border-0">
      <span className="shrink-0 font-mono text-[10px] text-default-600">[{ev.channel}]</span>
      <span className={`truncate font-mono text-[11px] ${colour}`}>{ev.message}</span>
      <span className="ml-auto shrink-0 font-mono text-[10px] text-default-600">
        {fmtTime(ev.ts)}
      </span>
    </div>
  );
}

export function WireLog() {
  const { events, clearEvents } = useLab();
  const [follow, setFollow] = useState(true);
  const [framesOnly, setFramesOnly] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const shown = useMemo(
    () => (framesOnly ? events.filter((e) => e.kind === "frame") : events.filter((e) => e.kind !== "state")),
    [events, framesOnly],
  );

  useEffect(() => {
    if (follow) endRef.current?.scrollIntoView({ block: "end" });
  }, [shown.length, follow]);

  const bytes = events.reduce((n, e) => n + (e.kind === "frame" ? e.bytes : 0), 0);

  return (
    <Card
      data-testid="wire-log"
      className="flex h-full flex-col border border-default-100/60 bg-default-50/30"
      shadow="none"
    >
      <CardHeader className="flex items-center gap-3 border-b border-default-100/60 py-2">
        <span className="text-sm font-medium">Wire</span>
        <span className="font-mono text-[10px] text-default-500">
          {shown.length} events · {fmtBytes(bytes)}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <Switch size="sm" isSelected={framesOnly} onValueChange={setFramesOnly}>
            <span className="text-[11px]">frames only</span>
          </Switch>
          <Switch size="sm" isSelected={follow} onValueChange={setFollow}>
            <span className="text-[11px]">follow</span>
          </Switch>
          <Button size="sm" variant="light" onPress={clearEvents}>
            clear
          </Button>
        </div>
      </CardHeader>

      <CardBody className="wire-scroll min-h-0 flex-1 overflow-y-auto p-0">
        {shown.length === 0 ? (
          <p className="p-4 text-xs text-default-500">
            Nothing yet. Run something from a panel and every frame in both directions shows up
            here.
          </p>
        ) : (
          shown.map((ev) =>
            ev.kind === "frame" ? (
              <FrameRow key={ev.seq} ev={ev} />
            ) : ev.kind === "gate" ? (
              <GateRow key={ev.seq} ev={ev} />
            ) : (
              <LogRow key={ev.seq} ev={ev as Extract<LabEvent, { kind: "log" }>} />
            ),
          )
        )}
        <div ref={endRef} />
      </CardBody>
    </Card>
  );
}
